import React, { createContext, useContext, useState, useEffect } from "react";

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    // Check localStorage first, then system preference
    const savedTheme = localStorage.getItem("theme");
    if (savedTheme) {
      return savedTheme;
    }

    // Check system preference
    if (
      window.matchMedia &&
      window.matchMedia("(prefers-color-scheme: light)").matches
    ) {
      return "light";
    }

    return "dark";
  });

  const [systemTheme, setSystemTheme] = useState(
    window.matchMedia &&
      window.matchMedia("(prefers-color-scheme: light)").matches
      ? "light"
      : "dark"
  );

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: light)");
    const handleChange = (e) => {
      setSystemTheme(e.matches ? "light" : "dark");
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  // Apply theme to document
  useEffect(() => {
    const effectiveTheme = theme === "auto" ? systemTheme : theme;
    document.documentElement.setAttribute("data-theme", effectiveTheme);
    localStorage.setItem("theme", theme);
  }, [theme, systemTheme]);

  const toggleTheme = () => {
    setTheme((prevTheme) => {
      if (prevTheme === "dark") return "light";
      if (prevTheme === "light") return "auto";
      return "dark";
    });
  };

  const setThemeMode = (newTheme) => {
    setTheme(newTheme);
  };

  const getEffectiveTheme = () => {
    return theme === "auto" ? systemTheme : theme;
  };

  const effectiveTheme = getEffectiveTheme();

  // Theme-aware utility classes
  // USAGE: const { themeClasses } = useTheme();
  // ALWAYS use these classes instead of hardcoded colors
  const themeClasses = {
    // Backgrounds
    bg: {
      // Main page background - use for page wrappers
      primary: effectiveTheme === "light" ? "bg-white" : "bg-black",
      // Secondary sections - use for sidebars, secondary areas
      secondary: effectiveTheme === "light" ? "bg-gray-50" : "bg-gray-900",
      // Tertiary elements - use for progress bars, subtle backgrounds
      tertiary: effectiveTheme === "light" ? "bg-gray-100" : "bg-gray-800",
      // Card backgrounds - use hologram-card for dark, clean cards for light
      card:
        effectiveTheme === "light"
          ? "bg-white rounded-2xl border border-gray-200 shadow-sm"
          : "bg-gray-900 rounded-2xl border border-gray-800 shadow-sm",
      cardHover:
        effectiveTheme === "light" ? "hover:bg-gray-50" : "hover:bg-gray-800",
      // Modal backgrounds
      modal: effectiveTheme === "light" ? "bg-white" : "bg-gray-900",
      // Form inputs - use for all input fields
      input:
        effectiveTheme === "light"
          ? "bg-white border-gray-300 focus:border-blue-500"
          : "bg-gray-800 border-gray-600 focus:border-blue-500",
      // PRIMARY BUTTON - use for main action buttons
      button:
        effectiveTheme === "light"
          ? "bg-blue-600 hover:bg-blue-700"
          : "bg-blue-600 hover:bg-blue-700",
      // SECONDARY BUTTON - use for secondary actions
      buttonSecondary:
        effectiveTheme === "light"
          ? "bg-gray-100 hover:bg-gray-200 border-gray-200"
          : "bg-gray-800 hover:bg-gray-700 border-gray-700",
    },

    // Text colors
    text: {
      // Main text - use for headers, primary content
      primary: effectiveTheme === "light" ? "text-gray-900" : "text-white",
      // Secondary text - use for descriptions, labels
      secondary: effectiveTheme === "light" ? "text-gray-600" : "text-gray-400",
      // Muted text - use for less important text
      tertiary: effectiveTheme === "light" ? "text-gray-500" : "text-gray-500",
      // Very muted text - use for placeholders, disabled text
      muted: effectiveTheme === "light" ? "text-gray-400" : "text-gray-600",
      // Accent color - use for links, highlights, icons
      accent: effectiveTheme === "light" ? "text-blue-600" : "text-blue-400",
      // Status colors
      success: effectiveTheme === "light" ? "text-green-600" : "text-green-400",
      warning:
        effectiveTheme === "light" ? "text-yellow-600" : "text-yellow-400",
      error: effectiveTheme === "light" ? "text-red-600" : "text-red-400",
    },

    // Borders
    border: {
      // Standard borders - use for cards, inputs, dividers
      primary:
        effectiveTheme === "light" ? "border-gray-200" : "border-gray-800",
      // Secondary borders - use for subtle separations
      secondary:
        effectiveTheme === "light" ? "border-gray-300" : "border-gray-700",
      // Accent borders - use for focus states, highlights
      accent:
        effectiveTheme === "light" ? "border-blue-500" : "border-blue-400",
      // Focus states - use in focus: pseudo-classes
      focus:
        effectiveTheme === "light"
          ? "focus:border-blue-500"
          : "focus:border-blue-400",
    },

    // Rings (focus states)
    ring: {
      primary:
        effectiveTheme === "light"
          ? "focus:ring-blue-500"
          : "focus:ring-blue-400",
    },

    // Shadows
    shadow: {
      card:
        effectiveTheme === "light"
          ? "shadow-lg shadow-gray-200/50"
          : "shadow-gray-900/50",
      modal:
        effectiveTheme === "light"
          ? "shadow-xl shadow-gray-300/50"
          : "shadow-gray-900/80",
    },
  };

  const value = {
    theme,
    systemTheme,
    effectiveTheme,
    toggleTheme,
    setTheme: setThemeMode,
    themeClasses,
    isDark: effectiveTheme === "dark",
    isLight: effectiveTheme === "light",
  };

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};
